import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Dropdown } from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import {
  PaymentCollectionOptions,
  PaymentCollectionType
} from '@features/group-management/constants';
import { PaymentCollectionComponentType, PaymentCollectionProps } from '@types';

import MonthCard from './components/MonthCard';
import OnceCard from './components/OnceCard';
import TermCard from './components/TermCard';
import WeekCard from './components/WeekCard';

const PaymentCollection = ({
  isEditGroup = false,
  type = PaymentCollectionComponentType.DEFAULT,
  cancelButtonLabel,
  confirmButtonLabel,
  cancelButtonStyle = '',
  confirmButtonStyle = '',
  handleConfirm,
  handleCancel,
  className,
  selectedtype,
  selectedFrequency,
  selectedDate,
  nextDueDate,
  isPaymentLinkSent,
  isImmediatePayment,
  isCustomTerm,
  source,
  isGroupMonthlyFirstDay
}: PaymentCollectionProps) => {
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const [selectedFilter, setSelectedFilter] = useState<PaymentCollectionType>(
    selectedtype || PaymentCollectionType.MONTHLY
  );

  const [activationDate, setActivationDate] = useState<string>();
  const [intervalFrequency, setIntervalFrequency] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showConsent, setShowConsent] = useState<boolean>(false);

  const handleFilterSelection = (filterValue: string) => {
    setSelectedFilter(filterValue as PaymentCollectionType);
  };

  const handleActivationDate = (date: string | Date) => {
    setActivationDate(date as string);
  };

  const handleIntervalFrequency = (frequency: string) => {
    setIntervalFrequency(frequency);
  };

  const renderCard = () => {
    switch (selectedFilter) {
      case PaymentCollectionType.MONTHLY:
        return (
          <MonthCard
            handleActivationDate={handleActivationDate}
            handleIntervalFrequency={handleIntervalFrequency}
            selectedDate={selectedDate}
            type={selectedtype}
            isPaymentLinkSent={isPaymentLinkSent}
            hideFirstDayFrequency={
              !(
                (orgFeatureFlags &&
                  orgFeatureFlags.includes(
                    FEATURE_FLAGS.ALLOW_FIRST_DAY_FREQUENCY
                  )) ||
                isGroupMonthlyFirstDay
              )
            }
          />
        );
      case PaymentCollectionType.WEEKLY:
        return (
          <WeekCard
            handleActivationDate={handleActivationDate}
            handleIntervalFrequency={handleIntervalFrequency}
            selectedDate={selectedDate}
            type={selectedtype}
            isPaymentLinkSent={isPaymentLinkSent}
          />
        );
      case PaymentCollectionType.BY_TERM:
        return (
          <TermCard
            handleActivationDate={handleActivationDate}
            handleIntervalFrequency={handleIntervalFrequency}
            selectedDate={selectedDate}
            selectedFrequency={selectedFrequency}
            type={selectedtype}
            isTermDisabled={
              type !== PaymentCollectionComponentType.DEFAULT &&
              source !== 'member'
            }
            nextDueDate={nextDueDate}
            isCustomTerm={isCustomTerm}
            hideAdvanced={
              source === 'member' ||
              !(
                orgFeatureFlags &&
                orgFeatureFlags.includes(FEATURE_FLAGS.CUSTOM_TERM)
              )
            }
          />
        );
      case PaymentCollectionType.ONCE:
        return (
          <OnceCard
            handleActivationDate={handleActivationDate}
            handleIntervalFrequency={handleIntervalFrequency}
            selectedDate={selectedDate}
            type={selectedtype}
            minDate={new Date()}
            isImmediatePayment={isImmediatePayment}
            isEdit={type !== PaymentCollectionComponentType.DEFAULT}
          />
        );
      default:
        return null;
    }
  };

  const getPaymentCollectionText = () => {
    if (type === PaymentCollectionComponentType.MONTHLY)
      return 'Collection frequency is currently set to monthly and cannot be changed';
    if (type === PaymentCollectionComponentType.WEEKLY)
      return 'Collection frequency is currently set to weekly and cannot be changed';
    if (type === PaymentCollectionComponentType.BY_TERM)
      return `Collection frequency is currently set for a term of ${intervalFrequency} months, and cannot be changed`;
    if (type === PaymentCollectionComponentType.ONCE)
      return 'Collection frequency is currently set as once and cannot be changed';
    return '';
  };

  useEffect(() => {
    if (type !== PaymentCollectionComponentType.DEFAULT)
      if (type === PaymentCollectionComponentType.MONTHLY)
        setSelectedFilter(PaymentCollectionType.MONTHLY);
      else if (type === PaymentCollectionComponentType.WEEKLY)
        setSelectedFilter(PaymentCollectionType.WEEKLY);
      else if (type === PaymentCollectionComponentType.BY_TERM)
        setSelectedFilter(PaymentCollectionType.BY_TERM);
      else if (type === PaymentCollectionComponentType.ONCE)
        setSelectedFilter(PaymentCollectionType.ONCE);
  }, [type]);

  useEffect(() => {
    if (selectedtype) setSelectedFilter(selectedtype);
  }, [selectedtype]);

  const getActivationDate = () => activationDate || selectedDate;

  const filteredOptions =
    source !== 'member'
      ? PaymentCollectionOptions
      : PaymentCollectionOptions.filter((option) => option.label !== 'Once');

  return (
    <div
      className={`flex w-105 flex-col justify-start rounded-lg !bg-white py-6 text-sm font-normal ${
        className || ''
      }`}
    >
      <div className="mb-4 w-full px-6 text-start text-base">
        Schedule payment collection
      </div>
      <div className="mb-4 border" />
      {type === PaymentCollectionComponentType.DEFAULT ||
      source === 'member' ? (
        <div className="justify start mb-4 flex items-center gap-4 px-6">
          <div className="text-grey">Frequency : </div>
          <Dropdown
            currentFilter={selectedFilter}
            filterOptions={filteredOptions}
            onSelectFilter={handleFilterSelection}
            className="!text-normal h-8.25 min-w-25 rounded-lg !bg-white"
            optionsStyle="!w-45.5"
            labelStyle="!font-normal"
            disabled={isEditGroup}
            usePortal
          />
        </div>
      ) : (
        <div
          className={`${
            type &&
            'mx-4 mb-4 flex items-center justify-center rounded-lg bg-grey10 px-4 py-2 text-grey'
          }`}
        >
          {getPaymentCollectionText()}
        </div>
      )}
      <div className="px-6">{renderCard()}</div>
      <div
        className={`border ${
          selectedFilter === PaymentCollectionType.ONCE ? 'mt-3' : ''
        }`}
      />
      <div className="mt-3 flex justify-end gap-3 px-6">
        <Button
          handleClick={handleCancel}
          variant="secondary"
          size="none"
          className={`h-11 w-25 text-base font-medium text-primary ${
            cancelButtonStyle && cancelButtonStyle
          }`}
        >
          {cancelButtonLabel}
        </Button>
        <Button
          handleClick={() =>
            handleConfirm({
              date: getActivationDate() as string,
              interval: intervalFrequency,
              type: selectedFilter,
              isFutureDate: showConsent
            })
          }
          variant="primary"
          size="none"
          className={`h-11 w-25 text-base font-medium text-white ${
            confirmButtonStyle && confirmButtonStyle
          }`}
        >
          {confirmButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default PaymentCollection;
